import CustomTable from "../../../../components/custom-table/custom-table";
import { TableHeader } from "../../../../components/custom-table/table";
import { isExceptionFlowForRadiologyOrg } from "../../../../enums/organizationTypes";
import { TableNames } from "../../../../enums/tableNames";
import GreenBullet from "../../../../svg/green-bullet";
import InfoBadge from "../../../infoBadge/infoBadge";
import { TableData } from "../types";

/** Props for the patient Dx codes table section. */
export interface PatientDxCodesSectionProps {
  /** Healthcare facility `category` used to hide the primary-Dx badge for radiology. */
  category?: string | null;
  tableData: TableData[];
  tableHeaders: TableHeader[];
  getRowClassName: (row: TableData) => string;
  /** Dx stacks the suggested primary code(s) were picked from (NCCN/CMS/Custom). */
  primarySources?: string[];
}

/**
 * Builds the primary Dx badge label from its source(s).
 *
 * Preserves the original "NCCN Guidelines" wording for the common NCCN-only
 * case, and names the actual source(s) (CMS, Custom, or a mix) otherwise.
 *
 * @param primarySources - Distinct sources of the suggested primary Dx code(s).
 * @return string: Badge text describing where the primary Dx code came from.
 */
const buildPrimaryDxBadgeText = (primarySources?: string[]): string => {
  const sources = primarySources?.length ? primarySources : ["NCCN"];
  const label =
    sources.length === 1 && sources[0] === "NCCN"
      ? "NCCN Guidelines"
      : sources.join(", ");
  return `Preferred Primary Dx Code as per ${label}`;
};

/**
 * Renders the patient Dx codes section with an optional primary-Dx source badge and table.
 *
 * @param props - Facility category, table data, headers, row className getter, and primary Dx source(s).
 */
export const PatientDxCodesSection = ({
  category,
  tableData,
  tableHeaders,
  getRowClassName,
  primarySources,
}: PatientDxCodesSectionProps) => (
  <div
    className={`flex flex-col pb-4 ${isExceptionFlowForRadiologyOrg(category) ? "" : "gap-3"}`}
  >
    <span className="text-h12 font-semibold leading-h12 text-primaryGray-1">
      Patient Dx Codes
    </span>
    {isExceptionFlowForRadiologyOrg(category) ? null : (
      <InfoBadge
        icon={<GreenBullet />}
        text={buildPrimaryDxBadgeText(primarySources)}
        bgColor="bg-tertiaryGreen-50"
        borderColor="border-tertiaryGreen-700"
        borderWidth="border-very-thin"
        textColor="text-primaryGray-2"
        width="w-80"
        height="h-8"
      />
    )}

    <CustomTable
      tableName={TableNames.CLINICAL_DATA}
      tableHeaders={tableHeaders}
      tableData={tableData}
      hideSearchBar={true}
      count={tableData.length}
      pagesPerView={4}
      itemsPerPage={12}
      isFetching={false}
      rowClassName={getRowClassName}
    />
  </div>
);
